import { useState, useEffect, useCallback } from 'react'
import { useModal } from '../../contexts/ModalContext'
import MarkdownText from '../MarkdownText'
import JournalEntryForm from '../forms/JournalEntryForm'

// Private per-character handouts from the DM. A player sees only revealed
// entries and never receives dm_notes at all — functions/api/journal.js drops
// the key server-side, so there is no client-side filtering to get wrong here.
function CharacterJournal({ characterId, canEdit }) {
  const { confirm, alert: showAlert } = useModal()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingEntry, setEditingEntry] = useState(null)
  const [isCreating, setIsCreating] = useState(false)

  const load = useCallback(() => {
    if (!characterId) return
    setLoading(true)
    fetch(`/api/journal?character_id=${characterId}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load journal'))))
      .then((rows) => {
        setEntries(Array.isArray(rows) ? rows : [])
        setError('')
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [characterId])

  useEffect(() => {
    load()
  }, [load])

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleEdit = (entry) => {
    setEditingEntry(entry)
  }

  const handleFormSubmit = async (formData) => {
    const isEdit = !!editingEntry
    const response = await fetch('/api/journal', {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        isEdit ? { ...formData, id: editingEntry.id } : { character_id: characterId, ...formData }
      ),
    })

    if (response.ok) {
      load()
      showAlert({
        title: isEdit ? 'Entry Updated' : 'Entry Created',
        message: `"${formData.title}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
        type: 'success',
      })
      setEditingEntry(null)
      setIsCreating(false)
    } else {
      const error = await response.json()
      showAlert({
        title: 'Error',
        message: error.error || `Failed to ${isEdit ? 'update' : 'create'} entry`,
        type: 'error',
      })
    }
  }

  // Optimistic, reverted on failure — the same shape DescriptionSections uses
  // for its single-block reveal toggle.
  const toggleVisible = async (entry) => {
    const next = entry.is_visible ? 0 : 1
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, is_visible: next } : e)))
    const response = await fetch('/api/journal', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: entry.id, is_visible: next }),
    })
    if (!response.ok) {
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, is_visible: entry.is_visible } : e))
      )
      showAlert({ title: 'Error', message: 'Failed to update visibility', type: 'error' })
    }
  }

  const handleDelete = async (entry) => {
    await confirm({
      title: 'Delete Entry',
      message: `Are you sure you want to delete "${entry.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const response = await fetch('/api/journal', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: entry.id }),
        })
        if (response.ok) {
          setEntries((prev) => prev.filter((e) => e.id !== entry.id))
          showAlert({ title: 'Entry Deleted', message: `"${entry.title}" has been deleted.`, type: 'success' })
        } else {
          const error = await response.json()
          showAlert({ title: 'Error', message: error.error || 'Failed to delete entry', type: 'error' })
        }
      },
    })
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading journal…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
        <p style={{ color: '#e74c3c' }}>Error loading journal: {error}</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>
          {canEdit
            ? 'Private handouts for this character. Hidden until you reveal them.'
            : 'Things you alone have seen. Re-read them whenever you like.'}
        </p>
        {canEdit && (
          <button
            onClick={handleCreate}
            style={{
              background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
              color: '#ffffff',
              border: '1px solid #27ae60',
              padding: '0.8rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              whiteSpace: 'nowrap',
            }}
          >
            + Add Entry
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📓</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Entries Yet</h3>
          <p style={{ color: 'var(--text-muted)' }}>
            {canEdit
              ? 'No entries yet for this character.'
              : 'Nothing here yet. Your DM will add to this in time.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {entries.map((entry) => (
            <article
              key={entry.id}
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '1.5rem',
                opacity: canEdit && !entry.is_visible ? 0.7 : 1,
              }}
            >
              <header
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  marginBottom: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.15rem' }}>
                    {entry.title}
                  </h3>
                  {entry.label && (
                    <span style={{ color: 'var(--accent-teal)', fontSize: '0.85rem' }}>
                      {entry.label}
                    </span>
                  )}
                  {canEdit && !entry.is_visible && (
                    <span
                      style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.65rem',
                        background: '#6b7f91',
                        color: 'var(--text-primary)',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '3px',
                        fontWeight: 600,
                      }}
                    >
                      Hidden from player
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      title={entry.is_visible ? 'Hide from player' : 'Reveal to player'}
                      onClick={() => toggleVisible(entry)}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '0.4rem 0.6rem',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                      }}
                    >
                      {entry.is_visible ? '👁️' : '👁️‍🗨️'}
                    </button>
                    <button
                      title="Edit"
                      onClick={() => handleEdit(entry)}
                      style={{
                        background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                        color: '#ffffff',
                        border: '1px solid var(--accent-teal)',
                        borderRadius: '6px',
                        padding: '0.4rem 0.6rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      title="Delete"
                      onClick={() => handleDelete(entry)}
                      style={{
                        background: 'linear-gradient(135deg, #a93226 0%, #c0392b 100%)',
                        color: '#ffffff',
                        border: '1px solid #e74c3c',
                        borderRadius: '6px',
                        padding: '0.4rem 0.6rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </header>

              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <MarkdownText>{entry.body}</MarkdownText>
              </div>

              {canEdit && entry.dm_notes && (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px dashed var(--border-color)',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                    }}
                  >
                    🔒 DM notes
                  </div>
                  <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    <MarkdownText>{entry.dm_notes}</MarkdownText>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <JournalEntryForm
        isOpen={isCreating || !!editingEntry}
        onClose={() => {
          setIsCreating(false)
          setEditingEntry(null)
        }}
        entry={editingEntry}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default CharacterJournal
